import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  QueryList,
  ViewChildren,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import { trigger, transition, animate, style } from "@angular/animations";

import { Table, TableModule } from "primeng/table";

import {
  DataElement,
  PaginationModel,
  PaginationParams,
} from "src/app/shared/models/common";
import { UiService } from "src/app/shared/services/ui.service";
import { UiUser } from "src/app/shared/models/ui-user.model";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { FormsModule } from "@angular/forms";
import { CheckboxWrapComponent } from "../checkbox-wrap.component";
import { LoaderComponent } from "../loader/loader.component";
import { SlideInOutComponent } from "../slide-in-out.component";

interface ColSet {
  title?: string;
  key?: string;
  colClass?: string;
  isTrueIcon?: string;
  isFalseIcon?: string;
  isImage?: boolean;
  permission?: boolean;
}

interface Templates extends ColSet {
  afterColumn?: number;
  template?: TemplateRef<any>;
  titleTemplate?: TemplateRef<any>;
}
@Component({
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    TranslateModule,
    FormsModule,
    CheckboxWrapComponent,
    LoaderComponent,
    SlideInOutComponent,
  ],
  selector: "ui-table",
  templateUrl: "./table.component.html",
  animations: [
    trigger("delayLeave", [
      transition(":enter", [
        style({ transform: "translateY(0%)" }),
        animate("0ms ease-in", style({ transform: "translateY(0%)" })),
      ]),
      transition(":leave", [
        animate("200ms ease-in", style({ transform: "translateY(0%)" })),
      ]),
    ]),
  ],
})
export class TableComponent implements OnInit, OnChanges {
  constructor(private uiService: UiService, private cdr: ChangeDetectorRef) { }
  tableEnhance(tbody) {
    this.tableEnhance = null;
    //this.uiService.arrowNav(tbody, tbody.querySelectorAll("button, a, input"));
  }

  @ViewChild("uiTable", { static: false }) set ft(table: Table) {
    if (table) {
      this.tableRef = table;
      // table ui enhance (arrow navigation etc.)
      const tbody = table.el.nativeElement.querySelector(".ui-table-tbody");
      this.tableEnhance && this.tableEnhance(tbody);
      // alter p-table api on table refresh
      if (this.requestPTableApiUpdate && !this.requestUpdate.observers.length) {
        // Only reset first if it was explicitly provided as input (new behavior)
        // Otherwise, don't touch first - let PrimeNG maintain its state (old behavior for backward compatibility)
        if (this.firstInputProvided) {
          table.first = this.first;
        }
        // If firstInputProvided is false, don't reset - preserve table's current state
        this.requestPTableApiUpdate = false;
      } else if (this.firstInputProvided && this.first !== undefined) {
        // Update first when it changes externally (only if input was provided)
        table.first = this.first;
      }
      if (this.totalRecords) {
        table.totalRecords = this.totalRecords;
      }
    }
  }

  @Input("data") set data(data: Object[]) {
    this._data = data;
    this.requestPTableApiUpdate = true;
    this.imageTypeCache.clear();
  }
  @Input() totalRecords: number = null;
  @Input() numberOfRows: number = 15;
  @Input() displayFilter: string[] = [];
  @Input() mobileFilter: string[] = [];
  @Input() templates: Templates[] = [];
  @Input() more: TemplateRef<any>;
  @Input() isTrueIcon: string = "check";
  @Input() isFalseIcon: string = "";
  @Input() orderBy: string;
  @Input() sortOrder: 1 | -1 = -1;
  @Input() sorthableColumns: boolean;
  @Input() title: string;
  @Input() selectedRow: Object;
  @Input() topEnd: TemplateRef<any>;
  @Input() belowTitle: TemplateRef<any>;
  @Input() aboveTableHeader: TemplateRef<any>;
  @Input() footerTemplate: TemplateRef<any>;
  @Input() colSet: ColSet[];
  @Input() isWrapped: boolean = false;
  @Input() allowClickableRow: boolean = true;
  @Input() prepend: boolean;
  @Input() sortable: boolean;
  @Input() wrapperClass: string;
  @Input() dense: boolean;
  @Input() tblColor: boolean;
  @Input() tblRoundBottom: boolean;
  @Input() paginatorTxt: boolean = true;
  @Input() rowHover: boolean = true;
  @Input() showPaginator: boolean = true;
  @Input() paginatorPosition: "top" | "bottom" | "both" = "both";
  @Input() scrollable: boolean = false;
  @Input() scrollHeight: string;
  @Input() highlightOnHover: boolean = true;
  @Input() checkRows: boolean;
  @Input() showHeaders: boolean = true;
  @Input() rowId: string;

  @Output() requestUpdate = new EventEmitter();
  @Output() onRowSelect = new EventEmitter();
  @Output() onMoreOpen = new EventEmitter();
  @Output() onMoreClose = new EventEmitter();
  @Output() onColumnSortRequest = new EventEmitter<[string, boolean]>();
  @Output() onPaginate = new EventEmitter();
  @Output() onToggleAllRows = new EventEmitter<boolean>();
  @Output() onRowChecked = new EventEmitter<any>();

  _data: Object[];
  @Input() first: number = 0;
  private firstInputProvided: boolean = false;
  rangeFrom: number;
  rangeTo: number;
  templatesFiltered: Templates[];
  colSpan: number = 0;
  observers: boolean;
  tableUiStartOnce: boolean = true;
  openCache: Object[] = [];
  openCacheIds: Set<any> = new Set();
  tableDomEnhance: any;
  requestPTableApiUpdate: boolean;
  preventTableRefresh: boolean;
  currentClickableTy: number;
  currentClickableTemplate: number;
  displayFilterSorrtToggler: Map<string, boolean> = new Map();
  tableRef: Table;
  toggleAllSw: boolean;
  // precomputed per-column lookups — the template renders per cell on every
  // CD cycle, so it must not run find() scans there
  templateFirstCol: Templates;
  templateAtCol: Templates[] = [];
  colSetsForFilter: ColSet[] = [];
  private initialized: boolean = false;
  // string cells repeat across CD cycles — cache the mime probe per value,
  // cleared whenever the data input changes
  private imageTypeCache: Map<string, boolean> = new Map();

  ngOnInit() {
    this.displayFilter = this.displayFilter.filter((item) => item);
    this.templates = this.templates.filter(
      (t) => t.template && t.permission !== false
    );
    this.templatesFiltered = this.templates.filter(
      (t) => typeof t.afterColumn !== "number"
    );
    this.colSpan = this.templates.length + this.displayFilter.length || 1;
    this.observers = !!this.onRowSelect.observers.length;
    if (this.more) this.colSpan += 1;
    if (this.colSet && this.templates)
      this.colSet = this.colSet.concat(this.templates);
    this.displayFilter.forEach((e) =>
      this.displayFilterSorrtToggler.set(e, false)
    );
    this.templatesFiltered.forEach((e) =>
      this.displayFilterSorrtToggler.set(e.key, false)
    );
    this.rebuildColumnLookups();
    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.tableDomEnhance = null;
    // Update table.first when first input changes
    if (changes['first'] && this.tableRef && changes['first'].currentValue !== undefined) {
      this.tableRef.first = changes['first'].currentValue;
    }
    // keep the precomputed lookups aligned if a parent swaps the column
    // inputs after init (before init, ngOnInit builds them)
    if (
      this.initialized &&
      (changes["displayFilter"] || changes["colSet"] || changes["templates"])
    ) {
      this.rebuildColumnLookups();
    }
  }

  private rebuildColumnLookups() {
    this.templateFirstCol = this.getTemplateByColumn(-1);
    this.templateAtCol = this.displayFilter.map((td, i) =>
      this.getTemplateByColumn(i)
    );
    this.colSetsForFilter = this.displayFilter.map((td) => this.getColSet(td));
  }

  ngOnDestroy() {
    this.tableDomEnhance = null;
  }

  reset() {
    this.tableRef.reset();
    this.openCache = [];
    this.openCacheIds.clear();
  }

  // single dispatch for the body-cell ngSwitch — replaces the chain of
  // is* probes that ran 7 checks (incl. two mime lookups) per cell per CD
  cellType(cell: any): string {
    if (cell === null || cell === undefined) return null;
    const type = typeof cell;
    if (type === "boolean") return "bool";
    if (type === "string") {
      if (cell.indexOf("translate") === 0) return "translatable";
      let isImg = this.imageTypeCache.get(cell);
      if (isImg === undefined) {
        isImg = this.uiService.isImage(cell);
        this.imageTypeCache.set(cell, isImg);
      }
      return isImg ? "image" : "string";
    }
    if (type === "number") return "string";
    if (cell instanceof DataElement) return "dataElement";
    if (cell instanceof UiUser) return "uiUser";
    if (cell instanceof Date) return "date";
    return null;
  }

  getTemplateByColumn(i: number) {
    const template = this.templates.find((t) => t.afterColumn == i);
    if (template && template.permission) return template;
    else return null;
  }

  private paginateTable(
    event: PaginationModel,
    callback: Function = null
  ): PaginationParams {
    const paginationParams: PaginationParams = {};
    let suffix = event.first === 0 ? 0 : 1;
    suffix = 1;
    paginationParams.rangeFrom = event.first + suffix;
    paginationParams.rangeTo = event.first + this.numberOfRows;
    if (typeof callback === "function") callback();
    return paginationParams;
  }

  outputPaginationData($event) {
    this.requestUpdate.emit(Object.assign(this.paginateTable($event)));
  }

  onPagination() {
    this.tableDomEnhance = null;
    if (this.onPaginate) {
      this.onPaginate.emit();
    }
  }

  private addToOpenCache(row) {
    this.openCache.push(row);
    // Also track by unique identifier if available
    const id = this.getRowId(row);
    if (id !== undefined && id !== null) {
      this.openCacheIds.add(id);
    }
  }

  private removeFromOpenCache(row) {
    let openCacheRowIndex: number = this.openCache.findIndex((r) => r === row);
    if (openCacheRowIndex !== -1) this.openCache.splice(openCacheRowIndex, 1);
    // Also remove by unique identifier if available
    const id = this.getRowId(row);
    if (id !== undefined && id !== null) {
      this.openCacheIds.delete(id);
    }
  }

  private getRowId(row: any): any {
    // Use the specified rowId property if provided
    if (this.rowId && row?.[this.rowId] !== undefined && row?.[this.rowId] !== null) {
      return row[this.rowId];
    }
  }

  isInOpenCache(row): boolean {
    // First check by object reference (for same instance)
    if (this.openCache.find((r) => r === row)) {
      return true;
    }
    // Then check by unique identifier (for new instances of same row)
    const id = this.getRowId(row);
    if (id !== undefined && id !== null) {
      return this.openCacheIds.has(id);
    }
    // Fallback to object comparison
    return !!this.openCache.find((r) => r == row);
  }

  public toggleShowMore(row, event?: MouseEvent) {
    event && event.stopPropagation();
    if (!this.isInOpenCache(row)) {
      this.addToOpenCache(row);
      this.onMoreOpen.emit(row);
    } else {
      this.removeFromOpenCache(row);
      this.onMoreClose.emit(row);
    }
    // Trigger change detection to update the view
    this.cdr.detectChanges();
  }

  rowSelected(row) {
    if (!this.observers) {
      if (this.more && this.allowClickableRow) {
        this.toggleShowMore(row);
      }
    } else {
      this.selectedRow = row;
      this.onRowSelect.emit(row);
    }
  }

  highlightRow(row) {
    return row === this.selectedRow;
  }

  getColSet(key: string): ColSet {
    if (!this.colSet) return {};
    return this.colSet.find((c) => c.key === key) || {};
  }

  onSort(th, i, mouseEvent, isTemplate: boolean) {
    let bool = this.displayFilterSorrtToggler.get(th);
    const addRemove: string = bool ? "add" : "remove";
    mouseEvent.currentTarget.classList[addRemove]("is-descendingOrder");

    if (isTemplate) {
      this.currentClickableTemplate = i;
      this.currentClickableTy = null;
    } else {
      this.currentClickableTy = i;
      this.currentClickableTemplate = null;
    }

    this.displayFilterSorrtToggler.set(th, !bool);

    if (isTemplate) {
      this.onColumnSortRequest.emit([(th as Templates).key, bool]);
    } else {
      this.onColumnSortRequest.emit([th, bool]);
    }
  }

  onToggleAll() {
    this._data.forEach((r) => (r["selected"] = this.toggleAllSw));
    this.onToggleAllRows.emit(this.toggleAllSw);
  }

  onRowCheck(row) {
    this.onRowChecked.emit(row);
  }
}
